import { DndContext, type DragEndEvent, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { motion } from 'framer-motion';
import { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { CheckIcon, ChevronDownIcon, DotsGridIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import { Radio, RadioGroup } from '../../../components/radio-group/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../components/select/index';
import { useLocale } from '../../../hooks/index';
import { stakeholderTypeTranslation } from '../../../translations/stakeholder-translation.util';
import {
  CapTableOrdering,
  CapTableOrderingMode,
  capTableOrderingModeDescription,
  capTableOrderingModeTitle,
  WithinTypeSort,
  withinTypeSortTitle,
} from '../../../types/snapshot.types';
import { StakeholderType } from '../../../types/stakeholder.types';
import { CAP_TABLE_ROW_EXPAND_TRANSITION } from '../components/cap-table-table-animations';
import { applyVisibleTypeOrder } from '../utils/sort-cap-table-rows.util';

// Presentation order, which `Object.values` does not give: the enum members are declared
// alphabetically, and the design opens with Default and with the ownership rules.
const ORDERING_MODES: CapTableOrderingMode[] = [
  CapTableOrderingMode.DEFAULT,
  CapTableOrderingMode.CUSTOM,
];

const WITHIN_TYPE_SORTS: WithinTypeSort[] = [
  WithinTypeSort.OWNERSHIP_DESC,
  WithinTypeSort.OWNERSHIP_ASC,
  WithinTypeSort.ALPHABETICAL_ASC,
  WithinTypeSort.ALPHABETICAL_DESC,
];

type DetailsOrderingTabProps = {
  /** The stakeholder types this company's cap table actually holds — the only ones worth dragging. */
  availableTypes: StakeholderType[];
  onChange: (ordering: CapTableOrdering) => void;
  ordering: CapTableOrdering;
};

type StakeholderTypeOrderItemProps = {
  isLast: boolean;
  label: string;
  type: StakeholderType;
};

const StakeholderTypeOrderItem: FC<StakeholderTypeOrderItemProps> = ({ isLast, label, type }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } =
    useSortable({ id: type });
  const { messagesLocale } = useLocale();

  return (
    <div
      className={twMerge(
        'flex w-full items-center gap-3 bg-white px-3 pb-[13px] pt-3',
        !isLast && 'border-b border-gray-50',
        isDragging && 'relative z-1 rounded-lg shadow-md',
        isOver && !isDragging && 'bg-gray-50',
      )}
      ref={setNodeRef}
      // dnd-kit's own transition drives the settle after a drop, and an inline `transition` would
      // otherwise replace the whole shorthand and swallow a class-based colour fade.
      style={{
        transform: CSS.Transform.toString(transform),
        transition: [transition, 'background-color 150ms ease-out, box-shadow 150ms ease-out']
          .filter(Boolean)
          .join(', '),
      }}
    >
      <button
        className="flex cursor-grab touch-none items-center text-gray-400 transition-opacity duration-150 hover:opacity-70 active:cursor-grabbing"
        type="button"
        {...listeners}
        {...attributes}
        aria-label={messagesLocale['CAP_TABLE_ORDERING_DRAG_TO_REORDER']}
      >
        <DotsGridIcon className="size-4" iconColor="#475467" />
      </button>
      <span className="text-xs font-[450] text-gray-700">{label}</span>
    </div>
  );
};

export const DetailsOrderingTab: FC<DetailsOrderingTabProps> = ({
  availableTypes,
  onChange,
  ordering,
}) => {
  const { messagesLocale } = useLocale();

  // A drag needs to travel before it counts, otherwise a click on the handle starts one.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  const isCustom = ordering.mode === CapTableOrderingMode.CUSTOM;

  // The saved sequence covers every type so that nothing is lost, but only the types the company
  // actually has are worth showing — the rest would be rows the viewer can never see the effect of.
  const visibleTypeOrder = useMemo(
    () => ordering.typeOrder.filter((type) => availableTypes.includes(type)),
    [availableTypes, ordering.typeOrder],
  );

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    if (!over || active.id === over.id) return;

    const fromIndex = visibleTypeOrder.indexOf(active.id as StakeholderType);
    const toIndex = visibleTypeOrder.indexOf(over.id as StakeholderType);
    if (fromIndex === -1 || toIndex === -1) return;

    onChange({
      ...ordering,
      typeOrder: applyVisibleTypeOrder(
        ordering.typeOrder,
        arrayMove(visibleTypeOrder, fromIndex, toIndex),
      ),
    });
  };

  return (
    <RadioGroup
      className="flex-col gap-3 divide-x-0 overflow-visible rounded-none"
      onChange={(mode: CapTableOrderingMode) => onChange({ ...ordering, mode })}
      value={ordering.mode}
      wrapperClassName="gap-0"
    >
      {ORDERING_MODES.map((mode) => (
        <div
          className={twMerge(
            'flex w-full flex-col rounded-lg border border-gray-100 bg-white p-3 transition-colors duration-200 ease-out',
            ordering.mode === mode && 'bg-brand-25',
          )}
          key={mode}
        >
          <Radio
            className="w-full cursor-pointer items-center justify-start gap-[10px]"
            styleType="NONE"
            value={mode}
          >
            <span className="flex size-4 shrink-0 items-center justify-center overflow-hidden rounded-full border border-gray-300 transition-colors duration-200 ease-out group-data-[checked]:border-none group-data-[checked]:bg-brand-500">
              <CheckIcon
                className="hidden size-[10px] group-data-[checked]:block"
                iconColor="#ffffff"
              />
            </span>
            <span className="flex flex-col items-start">
              <span className="text-sm font-[550] text-gray-700 transition-colors duration-200 ease-out group-data-[checked]:text-brand-700">
                <AppFormattedMessage id={capTableOrderingModeTitle[mode]} />
              </span>
              <span className="text-xs font-[450] text-gray-500">
                <AppFormattedMessage id={capTableOrderingModeDescription[mode]} />
              </span>
            </span>
          </Radio>

          {mode === CapTableOrderingMode.CUSTOM && (
            // Kept mounted and collapsed to zero height rather than unmounted, so switching
            // between Default and Custom slides instead of snapping. `inert` keeps the collapsed
            // list and dropdown out of the tab order.
            <motion.div
              animate={{ height: isCustom ? 'auto' : 0, opacity: isCustom ? 1 : 0 }}
              className="overflow-hidden"
              initial={false}
              transition={CAP_TABLE_ROW_EXPAND_TRANSITION}
            >
              <div
                aria-hidden={!isCustom}
                className="flex flex-col gap-[10px] pt-[10px]"
                inert={!isCustom}
              >
                <div className="flex w-full flex-col overflow-hidden rounded-lg">
                  <DndContext onDragEnd={handleDragEnd} sensors={sensors}>
                    <SortableContext
                      items={visibleTypeOrder}
                      strategy={verticalListSortingStrategy}
                    >
                      {visibleTypeOrder.map((type, index) => (
                        <StakeholderTypeOrderItem
                          isLast={index === visibleTypeOrder.length - 1}
                          key={type}
                          label={messagesLocale[stakeholderTypeTranslation[type]]}
                          type={type}
                        />
                      ))}
                    </SortableContext>
                  </DndContext>
                </div>

                <div className="flex w-full flex-col gap-2 rounded-lg bg-white p-3">
                  <span className="text-xs font-[450] text-gray-500">
                    <AppFormattedMessage id="CAP_TABLE_ORDERING_WITHIN_TYPE" />
                  </span>
                  <Select
                    onChange={(withinType: WithinTypeSort) => onChange({ ...ordering, withinType })}
                    value={ordering.withinType}
                  >
                    {({ open }) => (
                      <div className="relative w-full">
                        <SelectButton className="flex h-10 w-full items-center justify-between gap-[10px] rounded-none rounded-t border-0 border-b border-gray-300 bg-gray-50 px-[10px] text-sm font-[450] text-gray-700 transition-colors duration-150 ease-out hover:bg-gray-100">
                          <span className="truncate">
                            <AppFormattedMessage id={withinTypeSortTitle[ordering.withinType]} />
                          </span>
                          <ChevronDownIcon
                            className={twMerge(
                              'size-4 transition-transform duration-200 ease-out',
                              open && 'rotate-180',
                            )}
                            iconColor="#344054"
                          />
                        </SelectButton>
                        {/* Headless UI anchors the list in a portal beside the dialog, whose own
                            layer is `z-[61]` — anything lower opens behind the modal. */}
                        <SelectOptions
                          anchor={{ gap: 4, to: 'bottom start' }}
                          // `pointer-events-auto` is load-bearing: Radix's modal dialog sets
                          // `pointer-events: none` on <body> and re-enables it only inside the
                          // dialog, and this list is portalled to <body> beside it — without this
                          // the options cannot be clicked or hovered at all, only keyboarded.
                          // `cursor-pointer` sits on the panel rather than only the options so the
                          // padding and the gaps between rows do not flip back to an arrow.
                          className="pointer-events-auto z-[62] w-[var(--button-width)] cursor-pointer flex-col gap-1 text-nowrap rounded bg-white p-2 shadow-xl ring-0 transition duration-150 ease-out data-[closed]:-translate-y-1 data-[closed]:opacity-0"
                          transition
                        >
                          {WITHIN_TYPE_SORTS.map((withinType) => (
                            <SelectOption
                              className="h-auto w-full rounded px-2 py-[6px] transition-colors duration-100 ease-out"
                              key={withinType}
                              value={withinType}
                            >
                              <span className="block w-full truncate text-sm text-gray-700">
                                <AppFormattedMessage id={withinTypeSortTitle[withinType]} />
                              </span>
                            </SelectOption>
                          ))}
                        </SelectOptions>
                      </div>
                    )}
                  </Select>
                </div>
              </div>
            </motion.div>
          )}
        </div>
      ))}
    </RadioGroup>
  );
};
